import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import {
  UJIAN_BERTULIS_QUESTIONS,
  UjianBertulisQuestion,
  TOTAL_EXAM_QUESTIONS,
  TOTAL_BANK_QUESTIONS,
  ALLOCATED_TIME_MINUTES,
  ALLOCATED_TIME_SECONDS,
  MAX_ALLOWED_ATTEMPTS,
  PASSING_PERCENTAGE,
  PASSING_SCORE,
  UjianBertulisAttempt,
  calculateGrade,
  getRandomExamQuestions
} from '../../data/ujianBertulisQuestions';
import {
  getUjianBertulisAttemptsDB,
  saveUjianBertulisAttemptDB,
  deleteUjianBertulisAttemptDB,
  getMemberUjianBertulisSummaryDB,
  getPaidUjianCandidatesDB,
  getUjianBertulisKeyUnlockedDB,
  setUjianBertulisKeyUnlockedDB,
  getUjianBertulisCompletionStatusDB,
  UjianBertulisCompletionStatus,
  getMembersDB
} from '../../services/db';
import { PaidUjianCandidate, UserProfile } from '../../types';
import { GradingHeaderHero } from '../../components/grading/GradingHeaderHero';
import {
  formatPublicName,
  isManagementOrTrainer,
  isSuperAdmin,
  isChildOfParent,
  isFamilyMember
} from '../../utils/formatters';
import {
  Clock,
  Award,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ChevronRight,
  ChevronLeft,
  RotateCcw,
  ShieldCheck,
  FileText,
  Bookmark,
  Check,
  X,
  Printer,
  Sparkles,
  Trophy,
  Filter,
  Search,
  BookOpen,
  ArrowRight,
  ArrowLeft,
  User,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Users,
  Calendar,
  AlertTriangle,
  Play,
  Trash2,
  LayoutGrid,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface ActiveSessionStorage {
  userId: string;
  candidateId?: string;
  candidateName?: string;
  attemptNumber: 1 | 2;
  startedAt: string;
  secondsRemaining: number;
  answers: Record<number, number>;
  flaggedQuestionIds: number[];
  currentQuestionIndex: number;
  questionIds?: number[];
}

const SESSION_STORAGE_KEY = 'asmm_ujian_bertulis_session_v1';

export const UjianBertulisPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  // Page States: 'briefing' | 'exam' | 'results' | 'roster' (admin/management)
  const [viewState, setViewState] = useState<'briefing' | 'exam' | 'results' | 'roster'>('briefing');

  // Candidate & Family Members Management (Support answering as family member / child)
  const [familyMembers, setFamilyMembers] = useState<UserProfile[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<UserProfile | null>(null);

  // Active candidate resolved (either selected family member/child or currentUser)
  const activeCandidate: UserProfile = useMemo(() => {
    return selectedCandidate || currentUser || ({
      id: 'guest',
      name: 'Pesilat ASMM',
      gelanggang: 'Pusaka Warisan',
      bengkung: 'Putih'
    } as UserProfile);
  }, [selectedCandidate, currentUser]);

  // List of candidates available to the logged-in user (Self + Family / Children)
  const candidateOptions = useMemo(() => {
    if (!currentUser) return [];
    const list: {
      id: string;
      name: string;
      relationship: string;
      bengkung: string;
      gelanggang: string;
      isSelf: boolean;
      user: UserProfile;
    }[] = [];

    list.push({
      id: currentUser.id,
      name: currentUser.name,
      relationship: currentUser.role === 'Waris' ? 'Waris (Diri Sendiri)' : 'Diri Sendiri',
      bengkung: currentUser.bengkung || 'Putih',
      gelanggang: currentUser.gelanggang || 'Pusaka Warisan',
      isSelf: true,
      user: currentUser
    });

    familyMembers.forEach((f) => {
      if (f.id.toLowerCase() !== currentUser.id.toLowerCase()) {
        list.push({
          id: f.id,
          name: f.name,
          relationship: f.relationship || 'Anak',
          bengkung: f.bengkung || 'Putih',
          gelanggang: f.gelanggang || currentUser.gelanggang || 'Pusaka Warisan',
          isSelf: false,
          user: f
        });
      }
    });

    return list;
  }, [currentUser, familyMembers]);

  // Attempt Data for Selected Candidate
  const [userAttempts, setUserAttempts] = useState<UjianBertulisAttempt[]>([]);
  const [selectedAttemptForReview, setSelectedAttemptForReview] = useState<UjianBertulisAttempt | null>(null);
  const [isLoadingAttempts, setIsLoadingAttempts] = useState(true);

  // Active Exam State (50 Questions drawn from 115 questions bank)
  const [activeExamQuestionIds, setActiveExamQuestionIds] = useState<number[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [userAnswers, setUserAnswers] = useState<Record<number, number>>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(new Set());
  const [secondsRemaining, setSecondsRemaining] = useState<number>(ALLOCATED_TIME_SECONDS);
  const [examStartedAt, setExamStartedAt] = useState<string>('');
  const [currentAttemptNumber, setCurrentAttemptNumber] = useState<1 | 2>(1);

  // Question Palette State (Minimized by default)
  const [isPaletteExpanded, setIsPaletteExpanded] = useState<boolean>(false);
  const [paletteFilter, setPaletteFilter] = useState<'all' | 'unanswered' | 'flagged'>('all');

  // Confirmation Modals State (Lock body scroll)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isStartConfirmModalOpen, setIsStartConfirmModalOpen] = useState(false);
  const [attemptToDelete, setAttemptToDelete] = useState<UjianBertulisAttempt | null>(null);
  const [isDeletingAttempt, setIsDeletingAttempt] = useState(false);

  // Filter for review mode
  const [reviewFilter, setReviewFilter] = useState<'all' | 'wrong' | 'flagged'>('all');

  // Management Roster Data
  const [allAttemptsList, setAllAttemptsList] = useState<UjianBertulisAttempt[]>([]);
  const [rosterSearch, setRosterSearch] = useState('');
  const [rosterGelanggangFilter, setRosterGelanggangFilter] = useState('all');
  const [rosterStatusFilter, setRosterStatusFilter] = useState('all');

  // Cohort Completion Status & Anti-Cheating Answer Key Secrecy
  const [completionStatus, setCompletionStatus] = useState<UjianBertulisCompletionStatus>({
    totalCandidates: 0,
    completedCount: 0,
    remainingCount: 0,
    isAllCompleted: false,
    isKeyUnlocked: false,
    candidates: []
  });

  // Examiner / Management Preview Mode (allows examiners to inspect full key or test candidate view)
  const [isExaminerPreview, setIsExaminerPreview] = useState<boolean>(true);

  const timerRef = useRef<any>(null);

  // Management check
  const isManagement = useMemo(() => {
    return Boolean(currentUser && isManagementOrTrainer(currentUser));
  }, [currentUser]);

  const isUserSuperAdmin = useMemo(() => isSuperAdmin(currentUser), [currentUser]);

  // Lock body scroll whenever any modal is open (USER GLOBAL RULE)
  useEffect(() => {
    const isAnyModalOpen = isSubmitModalOpen || isStartConfirmModalOpen || Boolean(attemptToDelete);
    if (isAnyModalOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isSubmitModalOpen, isStartConfirmModalOpen, attemptToDelete]);

  // Load user attempts, cohort completion status & restore session if active
  const loadUserAttempts = async (targetCandidateId?: string) => {
    setIsLoadingAttempts(true);
    try {
      // 1. Fetch cohort completion status (whether all candidates have taken exam)
      const status = await getUjianBertulisCompletionStatusDB();
      setCompletionStatus(status);

      if (!currentUser) {
        setIsLoadingAttempts(false);
        return;
      }

      // Load all members to resolve family members
      const allMembers = await getMembersDB();
      const family = allMembers.filter(
        (m) =>
          (isChildOfParent(m, currentUser) || isFamilyMember(m, currentUser)) &&
          m.id.toLowerCase() !== currentUser.id.toLowerCase()
      );
      setFamilyMembers(family);

      // Resolve candidate to inspect
      let candToUse = selectedCandidate;
      if (targetCandidateId) {
        const found = [currentUser, ...family].find(
          (m) => m.id.toLowerCase() === targetCandidateId.toLowerCase()
        );
        if (found) candToUse = found;
      }

      if (!candToUse) {
        // If Waris and has family, default to first child
        if (currentUser.role === 'Waris' && family.length > 0) {
          candToUse = family[0];
        } else {
          candToUse = currentUser;
        }
      }
      setSelectedCandidate(candToUse);

      const effectiveId = candToUse.id;
      const summary = await getMemberUjianBertulisSummaryDB(effectiveId);
      setUserAttempts(summary.attempts);

      if (summary.latestAttempt && summary.attemptsCount > 0 && viewState !== 'exam') {
        setSelectedAttemptForReview(summary.latestAttempt);
      } else if (summary.attemptsCount === 0) {
        setSelectedAttemptForReview(null);
      }

      // Check if there's an ongoing active session saved
      const savedSessionRaw = localStorage.getItem(SESSION_STORAGE_KEY);
      if (savedSessionRaw) {
        try {
          const parsed: ActiveSessionStorage = JSON.parse(savedSessionRaw);
          const isUserMatch = parsed.userId === currentUser.id;
          const isCandMatch =
            parsed.candidateId &&
            (parsed.candidateId.toLowerCase() === effectiveId.toLowerCase() ||
              family.some((f) => f.id.toLowerCase() === parsed.candidateId?.toLowerCase()));

          if ((isUserMatch || isCandMatch) && parsed.secondsRemaining > 0) {
            // Restore active exam candidate if saved
            if (parsed.candidateId) {
              const matchedCandidate = [currentUser, ...family].find(
                (m) => m.id.toLowerCase() === parsed.candidateId?.toLowerCase()
              );
              if (matchedCandidate) {
                setSelectedCandidate(matchedCandidate);
              }
            }

            if (parsed.questionIds && parsed.questionIds.length > 0) {
              setActiveExamQuestionIds(parsed.questionIds);
            } else {
              const drawnIds = getRandomExamQuestions(UJIAN_BERTULIS_QUESTIONS, 50).map((q) => q.id);
              setActiveExamQuestionIds(drawnIds);
            }
            setUserAnswers(parsed.answers || {});
            setFlaggedQuestions(new Set(parsed.flaggedQuestionIds || []));
            setCurrentQuestionIndex(parsed.currentQuestionIndex || 0);
            setExamStartedAt(parsed.startedAt || new Date().toISOString());
            setCurrentAttemptNumber(parsed.attemptNumber || 1);
            setSecondsRemaining(parsed.secondsRemaining);
            setViewState('exam');
          }
        } catch { }
      }

      // If management, also fetch all attempts for roster tab
      if (isManagement) {
        const all = await getUjianBertulisAttemptsDB();
        setAllAttemptsList(all);
      }
    } catch (err) {
      console.error('Failed to load user ujian bertulis attempts:', err);
    } finally {
      setIsLoadingAttempts(false);
    }
  };

  // Candidate switcher handler
  const handleSelectCandidate = async (candidate: UserProfile) => {
    if (viewState === 'exam') {
      showToast('warning', 'Ujian Sedang Berjalan', 'Sila selesaikan ujian sebelum menukar calon.');
      return;
    }
    setSelectedCandidate(candidate);
    setIsLoadingAttempts(true);
    try {
      const summary = await getMemberUjianBertulisSummaryDB(candidate.id);
      setUserAttempts(summary.attempts);
      if (summary.latestAttempt && summary.attemptsCount > 0) {
        setSelectedAttemptForReview(summary.latestAttempt);
      } else {
        setSelectedAttemptForReview(null);
      }
    } catch (err) {
      console.error('Failed to switch candidate:', err);
    } finally {
      setIsLoadingAttempts(false);
    }
  };

  useEffect(() => {
    loadUserAttempts();
    const handleDbUpdated = () => {
      loadUserAttempts();
    };
    window.addEventListener('asmm_db_updated', handleDbUpdated);
    return () => window.removeEventListener('asmm_db_updated', handleDbUpdated);
  }, [currentUser, isManagement]);

  // Timer Effect for Active Exam
  useEffect(() => {
    if (viewState !== 'exam') {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleFinalizeSubmit(true); // Auto-submit when time expires!
          return 0;
        }

        const nextVal = prev - 1;

        // Sync to localStorage every 5 seconds to reduce write overhead
        if (nextVal % 5 === 0 && currentUser) {
          const sessionData: ActiveSessionStorage = {
            userId: currentUser.id,
            candidateId: activeCandidate.id,
            candidateName: activeCandidate.name,
            attemptNumber: currentAttemptNumber,
            startedAt: examStartedAt,
            secondsRemaining: nextVal,
            answers: userAnswers,
            flaggedQuestionIds: Array.from(flaggedQuestions),
            currentQuestionIndex,
            questionIds: activeExamQuestionIds
          };
          localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
        }

        return nextVal;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [viewState, userAnswers, flaggedQuestions, currentQuestionIndex, currentUser, activeCandidate, currentAttemptNumber, examStartedAt, activeExamQuestionIds]);

  // Derived metrics for selected candidate
  const attemptsCount = userAttempts.length;
  const hasRemainingAttempts = attemptsCount < MAX_ALLOWED_ATTEMPTS;

  const bestScoreAttempt = useMemo(() => {
    if (userAttempts.length === 0) return null;
    return [...userAttempts].sort((a, b) => b.percentage - a.percentage)[0];
  }, [userAttempts]);

  // Answer key visibility logic:
  // Visible to candidates ONLY when:
  // 1. All candidates taking Ujian Bengkung have completed at least 1 attempt (isAllCompleted === true), OR
  // 2. Administrator/Examiner manually unlocks the key (isKeyUnlocked === true)
  const isAnswerKeyRevealedToAll = completionStatus.isAllCompleted || completionStatus.isKeyUnlocked;
  // Examiners/Management can also toggle isExaminerPreview to view full key or simulate candidate view
  const isAnswerKeyVisible = isAnswerKeyRevealedToAll || (isManagement && isExaminerPreview);
  const completionPercentage = Math.round(
    (completionStatus.completedCount / (completionStatus.totalCandidates || 1)) * 100
  );

  const handleToggleManualKeyUnlock = async () => {
    if (!isManagement) return;
    const nextVal = !completionStatus.isKeyUnlocked;
    await setUjianBertulisKeyUnlockedDB(nextVal);
    setCompletionStatus((prev) => ({
      ...prev,
      isKeyUnlocked: nextVal
    }));
    showToast(
      nextVal ? 'success' : 'info',
      nextVal ? 'Skema Jawapan Dibuka' : 'Skema Jawapan Dikunci Semula',
      nextVal
        ? 'Semua calon kini boleh menyemak skema jawapan yang betul dan penjelasan silibus.'
        : 'Skema jawapan kini dirahsiakan semula daripada paparan calon.'
    );
  };

  // Format MM:SS for countdown timer
  const formattedTimer = useMemo(() => {
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [secondsRemaining]);

  // Timer urgency style
  const isTimeCritical = secondsRemaining <= 120; // under 2 mins
  const isTimeLow = secondsRemaining <= 600 && !isTimeCritical; // under 10 mins

  // Active 50 Exam Questions derived from activeExamQuestionIds
  const activeExamQuestions = useMemo(() => {
    if (activeExamQuestionIds.length === 0) {
      return UJIAN_BERTULIS_QUESTIONS.slice(0, 50);
    }
    const qMap = new Map<number, UjianBertulisQuestion>();
    UJIAN_BERTULIS_QUESTIONS.forEach((q) => qMap.set(q.id, q));
    const list = activeExamQuestionIds.map((id) => qMap.get(id)!).filter(Boolean);
    return list.length === 50 ? list : UJIAN_BERTULIS_QUESTIONS.slice(0, 50);
  }, [activeExamQuestionIds]);

  // Start Exam Handler (Draws 50 random questions from bank)
  const handleStartExam = () => {
    if (!currentUser) {
      showToast('error', 'Sila Log Masuk', 'Anda perlu log masuk untuk memulakan Ujian Bertulis.');
      return;
    }

    if (!hasRemainingAttempts) {
      showToast(
        'warning',
        'Had Percubaan Tamat',
        `Calon ${activeCandidate.name} telah menyempurnakan kedua-dua (2) percubaan yang diperuntukkan.`
      );
      return;
    }

    const nextAttemptNum: 1 | 2 = (attemptsCount + 1) as 1 | 2;
    setCurrentAttemptNumber(nextAttemptNum);
    setUserAnswers({});
    setFlaggedQuestions(new Set());
    setCurrentQuestionIndex(0);
    setSecondsRemaining(ALLOCATED_TIME_SECONDS);
    const nowIso = new Date().toISOString();
    setExamStartedAt(nowIso);

    // Draw 50 random questions from the 115 questions bank
    const drawnQuestions = getRandomExamQuestions(UJIAN_BERTULIS_QUESTIONS, 50);
    const drawnIds = drawnQuestions.map((q) => q.id);
    setActiveExamQuestionIds(drawnIds);

    // Save initial session
    const sessionData: ActiveSessionStorage = {
      userId: currentUser.id,
      candidateId: activeCandidate.id,
      candidateName: activeCandidate.name,
      attemptNumber: nextAttemptNum,
      startedAt: nowIso,
      secondsRemaining: ALLOCATED_TIME_SECONDS,
      answers: {},
      flaggedQuestionIds: [],
      currentQuestionIndex: 0,
      questionIds: drawnIds
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));

    setIsStartConfirmModalOpen(false);
    setViewState('exam');
    showToast(
      'info',
      'Ujian Bermula',
      `Percubaan ${nextAttemptNum} bermula untuk calon ${activeCandidate.name} (50 soalan dipilih secara rawak daripada bank soalan). Masa diperuntukkan adalah 75 minit.`
    );
  };

  // Option selection
  const handleSelectOption = (questionId: number, optionIdx: number) => {
    setUserAnswers((prev) => {
      const updated = { ...prev, [questionId]: optionIdx };
      // Save session immediately on answer
      if (currentUser) {
        const sessionData: ActiveSessionStorage = {
          userId: currentUser.id,
          candidateId: activeCandidate.id,
          candidateName: activeCandidate.name,
          attemptNumber: currentAttemptNumber,
          startedAt: examStartedAt,
          secondsRemaining,
          answers: updated,
          flaggedQuestionIds: Array.from(flaggedQuestions),
          currentQuestionIndex,
          questionIds: activeExamQuestionIds
        };
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
      }
      return updated;
    });
  };

  // Toggle Flag Question
  const handleToggleFlag = (questionId: number) => {
    setFlaggedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  // Finalize & Calculate Score
  const handleFinalizeSubmit = async (isAutoExpire = false) => {
    if (!currentUser) return;

    if (timerRef.current) clearInterval(timerRef.current);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setIsSubmitModalOpen(false);

    // Compute score and category breakdown using the 50 active questions
    let totalCorrect = 0;
    const catScores = {
      sahsiah: { correct: 0, total: 0 },
      silat: { correct: 0, total: 0 },
      persatuan: { correct: 0, total: 0 }
    };

    activeExamQuestions.forEach((q) => {
      const userChoice = userAnswers[q.id];
      const isCorrect = userChoice === q.correctAnswer;
      if (isCorrect) totalCorrect += 1;

      if (q.category === 'Sahsiah & Adab Kependekaran') {
        catScores.sahsiah.total += 1;
        if (isCorrect) catScores.sahsiah.correct += 1;
      } else if (q.category === 'Silat & Kemahiran Silibus') {
        catScores.silat.total += 1;
        if (isCorrect) catScores.silat.correct += 1;
      } else {
        // Sejarah & Falsafah ASMM
        catScores.persatuan.total += 1;
        if (isCorrect) catScores.persatuan.correct += 1;
      }
    });

    const percentage = Math.round((totalCorrect / TOTAL_EXAM_QUESTIONS) * 100);
    const isPassed = percentage >= PASSING_PERCENTAGE;
    const grade = calculateGrade(percentage);
    const timeSpent = ALLOCATED_TIME_SECONDS - secondsRemaining;

    const newAttempt: UjianBertulisAttempt = {
      id: `att-${activeCandidate.id}-${currentAttemptNumber}-${Date.now().toString().slice(-6)}`,
      attemptNumber: currentAttemptNumber,
      userId: activeCandidate.id,
      memberId: activeCandidate.id,
      userName: activeCandidate.name || 'Pesilat ASMM',
      userIcNo: activeCandidate.icNo,
      gelanggang: activeCandidate.gelanggang || 'Pusaka Warisan',
      bengkung: activeCandidate.bengkung || 'Putih',
      startedAt: examStartedAt,
      completedAt: new Date().toISOString(),
      timeSpentSeconds: timeSpent > 0 ? timeSpent : 1,
      score: totalCorrect,
      totalQuestions: TOTAL_EXAM_QUESTIONS,
      percentage,
      isPassed,
      grade,
      answers: userAnswers,
      questionIds: activeExamQuestions.map((q) => q.id),
      categoryBreakdown: {
        sahsiah: {
          correct: catScores.sahsiah.correct,
          total: catScores.sahsiah.total,
          percentage: Math.round((catScores.sahsiah.correct / (catScores.sahsiah.total || 1)) * 100)
        },
        silat: {
          correct: catScores.silat.correct,
          total: catScores.silat.total,
          percentage: Math.round((catScores.silat.correct / (catScores.silat.total || 1)) * 100)
        },
        persatuan: {
          correct: catScores.persatuan.correct,
          total: catScores.persatuan.total,
          percentage: Math.round((catScores.persatuan.correct / (catScores.persatuan.total || 1)) * 100)
        }
      }
    };

    try {
      await saveUjianBertulisAttemptDB(newAttempt);
      setSelectedAttemptForReview(newAttempt);
      setViewState('results');

      if (isAutoExpire) {
        showToast(
          'warning',
          'Masa 75 Minit Tamat',
          `Masa peperiksaan calon ${activeCandidate.name} telah tamat. Jawapan telah dihantar secara automatik.`
        );
      } else if (isPassed) {
        showToast(
          'success',
          'Tahniah! Calon Lulus',
          `Calon ${activeCandidate.name} memperolehi ${totalCorrect}/${TOTAL_EXAM_QUESTIONS} soalan betul (${percentage}% - Gred ${grade}).`
        );
      } else {
        showToast(
          'info',
          'Keputusan Dihantar',
          `Calon ${activeCandidate.name} mendapat ${totalCorrect}/${TOTAL_EXAM_QUESTIONS} soalan betul (${percentage}%). ${currentAttemptNumber < 2
            ? 'Calon masih mempunyai 1 percubaan lagi.'
            : 'Kedua-dua percubaan telah selesai.'
          }`
        );
      }

      // Reload fresh list for the active candidate
      const summary = await getMemberUjianBertulisSummaryDB(activeCandidate.id);
      setUserAttempts(summary.attempts);
    } catch (err) {
      console.error('Error saving attempt:', err);
      showToast('error', 'Ralat Menyimpan', 'Gagal menyimpan keputusan ujian ke pangkalan data.');
    }
  };

  // Superadmin Handler: Delete candidate attempt
  const handleConfirmDeleteAttempt = async () => {
    if (!attemptToDelete || !isUserSuperAdmin) return;
    setIsDeletingAttempt(true);
    try {
      await deleteUjianBertulisAttemptDB(attemptToDelete.id);
      showToast(
        'success',
        'Percubaan Dipadam',
        `Rekod Percubaan ${attemptToDelete.attemptNumber} bagi calon ${attemptToDelete.userName} telah berjaya dipadam dari sistem.`
      );

      // If currently reviewing this deleted attempt, reset review state
      if (selectedAttemptForReview?.id === attemptToDelete.id) {
        setSelectedAttemptForReview(null);
        setViewState(isManagement ? 'roster' : 'briefing');
      }

      // Reload fresh lists
      await loadUserAttempts(activeCandidate.id);
      setAttemptToDelete(null);
    } catch (err) {
      console.error('Failed to delete attempt:', err);
      showToast('error', 'Ralat Memadam', 'Gagal memadam rekod percubaan daripada sistem.');
    } finally {
      setIsDeletingAttempt(false);
    }
  };

  // Current question data
  const currentQuestion = activeExamQuestions[currentQuestionIndex] || activeExamQuestions[0];
  const answeredCount = Object.keys(userAnswers).length;

  // Resolve the exact 50 questions that were asked in this attempt
  const reviewAttemptQuestions = useMemo(() => {
    if (!selectedAttemptForReview) return [];
    const qMap = new Map<number, UjianBertulisQuestion>();
    UJIAN_BERTULIS_QUESTIONS.forEach(q => qMap.set(q.id, q));

    if (selectedAttemptForReview.questionIds && selectedAttemptForReview.questionIds.length > 0) {
      const fromIds = selectedAttemptForReview.questionIds.map(id => qMap.get(id)!).filter(Boolean);
      if (fromIds.length > 0) return fromIds;
    }
    // Fallback for legacy attempts: from answered question IDs or first 50
    const ansIds = Object.keys(selectedAttemptForReview.answers || {}).map(Number);
    if (ansIds.length > 0) {
      const fromAns = ansIds.map(id => qMap.get(id)!).filter(Boolean);
      if (fromAns.length > 0) return fromAns;
    }
    return UJIAN_BERTULIS_QUESTIONS.slice(0, 50);
  }, [selectedAttemptForReview]);

  // Review mode filtered questions
  const displayedReviewQuestions = useMemo(() => {
    if (!selectedAttemptForReview) return [];
    return reviewAttemptQuestions.filter((q) => {
      const userChoice = selectedAttemptForReview.answers[q.id];
      const isCorrect = userChoice === q.correctAnswer;
      // If answer key is not visible, do not allow filtering by 'wrong' to prevent deducing answers
      if (!isAnswerKeyVisible) {
        if (reviewFilter === 'flagged') return flaggedQuestions.has(q.id);
        return true;
      }
      if (reviewFilter === 'wrong') return !isCorrect;
      if (reviewFilter === 'flagged') return flaggedQuestions.has(q.id);
      return true;
    });
  }, [selectedAttemptForReview, reviewAttemptQuestions, reviewFilter, flaggedQuestions, isAnswerKeyVisible]);

  // Management filtered attempts
  const filteredRoster = useMemo(() => {
    return allAttemptsList.filter((att) => {
      // Search by name, IC, or memberId
      if (rosterSearch) {
        const query = rosterSearch.toLowerCase();
        const matchName = (att.userName || '').toLowerCase().includes(query);
        const matchId = (att.memberId || '').toLowerCase().includes(query);
        const matchIc = (att.userIcNo || '').replace(/\D/g, '').includes(query.replace(/\D/g, ''));
        if (!matchName && !matchId && !matchIc) return false;
      }
      // Gelanggang
      if (rosterGelanggangFilter !== 'all' && att.gelanggang !== rosterGelanggangFilter) {
        return false;
      }
      // Status
      if (rosterStatusFilter === 'passed' && !att.isPassed) return false;
      if (rosterStatusFilter === 'failed' && att.isPassed) return false;
      return true;
    });
  }, [allAttemptsList, rosterSearch, rosterGelanggangFilter, rosterStatusFilter]);

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-20">
      {/* ======================================================== */}
      {/* VIEW A: PRE-EXAM BRIEFING & ATTEMPT SELECTOR (ZERO-MODAL) */}
      {/* ======================================================== */}
      {viewState === 'briefing' && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-8 animate-fade-in">
          {/* Standardized Hero Feature Card */}
          <GradingHeaderHero
            activeTab="ujian-bertulis"
            badgeTag="Peperiksaan Teori Silat & Adab Kependekaran"
            badgeIcon={FileText}
            badgeColorClass="bg-blue-50 text-blue-700 border-blue-200/80"
            title="Ujian Bertulis & Teori Kependekaran"
            subtitle="Ujian bertulis objektif 50 soalan menguji pemahaman sahsiah adab kependekaran, sukatan kemahiran silibus ASMM, serta sejarah warisan & falsafah persilatan."
            metrics={[
              { value: 50, label: 'Soalan', colorClass: 'text-slate-900' },
              { value: '75', label: 'Minit', colorClass: 'text-red-600' },
              { value: '60%', label: 'Lulus Minima', colorClass: 'text-amber-600' },
              { value: '2 Kali', label: 'Percubaan', colorClass: 'text-blue-600' },
            ]}
          >
            {/* View State Switcher: Ujian Saya vs Rekod Calon */}
            {isManagement && (
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="inline-flex items-center gap-1 bg-slate-100 p-1 rounded-2xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setViewState('briefing')}
                    className="px-3.5 py-1.5 rounded-xl text-xs font-black transition cursor-pointer bg-slate-900 text-white shadow-xs"
                  >
                    Ujian Saya
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewState('roster')}
                    className="px-3.5 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 text-slate-600 hover:text-slate-900 hover:bg-white"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-amber-500" />
                    <span>Rekod Calon ({allAttemptsList.length})</span>
                  </button>
                </div>
              </div>
            )}
            {/* Candidate Selector for Family Members / Children */}
            {candidateOptions.length > 1 && (
              <div className="mt-4 pt-4 border-t border-slate-100 text-left max-w-3xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center font-bold">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                        Pilih Calon Ujian Bertulis
                      </h4>
                      <p className="text-[11px] text-slate-500 font-medium">
                        Boleh menjawab untuk diri sendiri atau tukar ke profil ahli keluarga.
                      </p>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-slate-600 px-2.5 py-0.5 rounded-full bg-slate-100 self-start sm:self-auto border border-slate-200/60">
                    {candidateOptions.length} Profil Tersedia
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                  {candidateOptions.map((cand) => {
                    const isSelected = activeCandidate.id.toLowerCase() === cand.id.toLowerCase();
                    return (
                      <button
                        key={cand.id}
                        type="button"
                        onClick={() => handleSelectCandidate(cand.user)}
                        className={`group flex items-center gap-3 p-3 rounded-2xl border text-left transition-all cursor-pointer relative ${isSelected
                          ? 'bg-red-50/70 border-red-500 ring-2 ring-red-100 shadow-xs'
                          : 'bg-slate-50/70 hover:bg-slate-100/80 border-slate-200/80 text-slate-700'
                          }`}
                      >
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-xs shrink-0 transition-colors ${isSelected
                            ? 'bg-red-600 text-white shadow-xs'
                            : 'bg-white text-slate-700 border border-slate-200 group-hover:bg-slate-200'
                            }`}
                        >
                          {cand.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-900 truncate">
                            {cand.name}
                          </div>
                          <div className="text-[10px] text-slate-500 font-semibold truncate flex items-center gap-1.5 mt-0.5">
                            <span
                              className={`px-1.5 py-0.2 rounded font-bold ${cand.isSelf ? 'bg-slate-200/80 text-slate-700' : 'bg-amber-100 text-amber-900'
                                }`}
                            >
                              {cand.relationship}
                            </span>
                            <span>•</span>
                            <span>{cand.bengkung}</span>
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-red-600 shrink-0 ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

          </GradingHeaderHero>

          {/* 3 Domain Topics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Domain 1: Sahsiah */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs hover:border-amber-300 hover:shadow-sm transition-all flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 border border-amber-200/70 flex items-center justify-center font-black text-sm">
                    A
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200 text-[11px] font-bold">
                    Bank: 50 Soalan
                  </span>
                </div>
                <h3 className="text-base font-bold text-slate-900">
                  Sahsiah & Adab Kependekaran
                </h3>
                <p className="text-xs text-slate-600 font-medium mt-1.5 leading-relaxed">
                  Merangkumi adab gelanggang, adab berguru, ikrar pesilat, nilai tawaduk, disiplin masa, etika silaturahim, dan pantang larang persilatan.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold">
                <span className="text-slate-500 font-semibold text-[11px]">
                  ~18 Soalan / Percubaan
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-black">
                  Wajaran 36%
                </span>
              </div>
            </div>

            {/* Domain 2: Silat */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs hover:border-red-300 hover:shadow-sm transition-all flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-red-50 text-red-700 border border-red-200/70 flex items-center justify-center font-black text-sm">
                    B
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200 text-[11px] font-bold">
                    Bank: 30 Soalan
                  </span>
                </div>
                <h3 className="text-base font-bold text-slate-900">
                  Silat & Kemahiran Silibus
                </h3>
                <p className="text-xs text-slate-600 font-medium mt-1.5 leading-relaxed">
                  Meliputi kekuda separuh tiang, jurus asas, buah pukulan, tangkisan, kuncian sendi, elakan luar/dalam, dan aplikasi senjata warisan.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold">
                <span className="text-slate-500 font-semibold text-[11px]">
                  ~18 Soalan / Percubaan
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-[11px] font-black">
                  Wajaran 36%
                </span>
              </div>
            </div>

            {/* Domain 3: Sejarah & Falsafah */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs hover:border-blue-300 hover:shadow-sm transition-all flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 border border-blue-200/70 flex items-center justify-center font-black text-sm">
                    C
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200 text-[11px] font-bold">
                    Bank: 35 Soalan
                  </span>
                </div>
                <h3 className="text-base font-bold text-slate-900">
                  Sejarah & Falsafah ASMM
                </h3>
                <p className="text-xs text-slate-600 font-medium mt-1.5 leading-relaxed">
                  Menghayati salasilah Patani, Srikandi Semerah, Pendekar Awang Sulung, falsafah Biduk Berlalu Kiambang Bertaut, dan makna 12 lambang rasmi.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold">
                <span className="text-slate-500 font-semibold text-[11px]">
                  ~14 Soalan / Percubaan
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-black">
                  Wajaran 28%
                </span>
              </div>
            </div>
          </div>

          {/* Past Attempts Summary & Launch CTA */}
          <div className="bg-slate-50 rounded-3xl border border-slate-200 p-6 sm:p-8 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-extrabold text-slate-900">
                  Status Rekod Percubaan Anda
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  Setiap peserta dibenarkan maksimum 2 kali percubaan. Markah terbaik diambil sebagai markah rasmi.
                </p>
              </div>

              {/* Action Button: Start or Limits reached */}
              <div>
                {hasRemainingAttempts ? (
                  <button
                    type="button"
                    onClick={() => setIsStartConfirmModalOpen(true)}
                    className="w-full sm:w-auto px-6 py-3.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-black text-sm tracking-wide shadow-md hover:shadow-lg transition flex items-center justify-center gap-2 cursor-pointer transform hover:-translate-y-0.5"
                  >
                    <Play className="w-4 h-4 fill-current" />
                    <span>
                      {attemptsCount === 0 ? 'Mula Ujian Bertulis Sekarang' : 'Ambil Percubaan Ke-2 (Terakhir)'}
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="px-4 py-2.5 rounded-full bg-slate-200 text-slate-700 text-xs font-extrabold flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5" />
                    <span>Had 2 Percubaan Telah Lengkap</span>
                  </div>
                )}
              </div>
            </div>

            {/* List of user's past attempts */}
            {userAttempts.length === 0 ? (
              <div className="bg-white rounded-2xl p-6 text-center border border-slate-200/80 space-y-2">
                <BookOpen className="w-8 h-8 text-slate-300 mx-auto" />
                <h4 className="text-sm font-bold text-slate-700">Belum Ada Rekod Percubaan</h4>
                <p className="text-xs text-slate-500 max-w-md mx-auto">
                  Anda belum menduduki sebarang ujian bertulis. Tekan butang di atas untuk memulakan Percubaan 1 anda.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {userAttempts.map((att) => {
                  const isBest = bestScoreAttempt && bestScoreAttempt.id === att.id;
                  const isPassed = att.isPassed;

                  return (
                    <div
                      key={att.id}
                      className={`bg-white rounded-2xl p-5 border relative transition ${isBest ? 'border-amber-400 ring-2 ring-amber-200/60 shadow-xs' : 'border-slate-200'
                        }`}
                    >
                      {isBest && (
                        <span className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full bg-amber-500 text-slate-950 font-black text-[10px] tracking-wide flex items-center gap-1 shadow-xs">
                          <Trophy className="w-3 h-3" />
                          Skor Terbaik Diiktiraf
                        </span>
                      )}

                      <div className="flex items-start justify-between">
                        <div>
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-900 text-white">
                            Percubaan {att.attemptNumber}
                          </span>
                          <span className="text-[11px] text-slate-400 ml-2">
                            {new Date(att.completedAt).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>

                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-black ${isPassed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                          {isPassed ? '✓ Lulus' : '✗ Mengulang'}
                        </span>
                      </div>

                      <div className="mt-4 flex items-baseline gap-2">
                        <span className="text-3xl font-black text-slate-900 tracking-tight">
                          {att.percentage}%
                        </span>
                        <span className="text-xs font-bold text-slate-500">
                          ({att.score} / {att.totalQuestions} Soalan Betul)
                        </span>
                      </div>

                      <div className="mt-1 text-xs font-semibold text-slate-600">
                        Gred: <span className="font-bold text-slate-900">{att.grade}</span> • Masa: {Math.floor(att.timeSpentSeconds / 60)}m {att.timeSpentSeconds % 60}s
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-[11px] text-slate-400 font-medium">
                          Sahsiah: {att.categoryBreakdown?.sahsiah?.percentage || 0}% • Silat: {att.categoryBreakdown?.silat?.percentage || 0}%
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedAttemptForReview(att);
                              setViewState('results');
                            }}
                            className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1 cursor-pointer"
                          >
                            <span>Semak Jawapan</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                          {isUserSuperAdmin && (
                            <button
                              type="button"
                              onClick={() => setAttemptToDelete(att)}
                              className="p-1 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition cursor-pointer"
                              title="Padam Percubaan Ini (Superadmin Sahaja)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* VIEW B: ACTIVE EXAM IN PROGRESS (75-MINUTE TIMER & QUESTIONS) */}
      {/* ======================================================== */}
      {viewState === 'exam' && currentQuestion && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-in">
          {/* Sticky Timer & Progress Topbar */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm sticky top-16 z-30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Left Question Indicator & Active Candidate */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-black tracking-wide">
                Soalan {currentQuestionIndex + 1} / {TOTAL_EXAM_QUESTIONS}
              </span>
              <span className="text-xs font-bold text-slate-500 hidden sm:inline">
                Dijawab: {answeredCount} / {TOTAL_EXAM_QUESTIONS} ({Math.round((answeredCount / TOTAL_EXAM_QUESTIONS) * 100)}%)
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-800 text-xs font-bold border border-slate-200">
                <User className="w-3.5 h-3.5 text-slate-500" />
                <span className="max-w-[130px] sm:max-w-[180px] truncate">{activeCandidate.name}</span>
                {activeCandidate.id.toLowerCase() !== currentUser?.id.toLowerCase() && (
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-amber-100 text-amber-900 font-extrabold">
                    {activeCandidate.relationship || 'Anak'}
                  </span>
                )}
              </span>
            </div>

            {/* Center / Right: Palette Toggle, Timer & Submit */}
            <div className="flex items-center justify-between sm:justify-end gap-2.5 flex-wrap">
              {/* Question Palette Toggle Pill */}
              <button
                type="button"
                onClick={() => setIsPaletteExpanded(!isPaletteExpanded)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5 cursor-pointer border ${isPaletteExpanded
                  ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                  : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200 shadow-2xs'
                  }`}
                title="Buka atau kecilkan palet soalan"
              >
                <LayoutGrid className="w-3.5 h-3.5 text-red-600" />
                <span className="hidden sm:inline">Palet Soalan</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${isPaletteExpanded ? 'bg-slate-800 text-amber-400' : 'bg-slate-100 text-slate-700'
                  }`}>
                  {answeredCount}/50
                </span>
                {isPaletteExpanded ? (
                  <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                )}
              </button>

              {/* 75-Minute Countdown Timer */}
              <div className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full font-mono font-black text-xs sm:text-sm transition-all border ${isTimeCritical
                ? 'bg-red-600 text-white border-red-700 animate-pulse ring-4 ring-red-100 shadow-md'
                : isTimeLow
                  ? 'bg-amber-100 text-amber-900 border-amber-300'
                  : 'bg-slate-100 text-slate-900 border-slate-200'
                }`}>
                <Clock className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isTimeCritical ? 'animate-spin' : ''}`} />
                <span>Baki: {formattedTimer}</span>
              </div>

              {/* Quick Submit Button */}
              <button
                type="button"
                onClick={() => setIsSubmitModalOpen(true)}
                className="px-4 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs shadow-xs transition cursor-pointer"
              >
                Hantar Ujian
              </button>
            </div>
          </div>

          {/* Linear Progress Bar */}
          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div
              className="bg-red-600 h-full transition-all duration-300 rounded-full"
              style={{ width: `${(answeredCount / TOTAL_EXAM_QUESTIONS) * 100}%` }}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Question Card (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-6">
                {/* Category Pill & Flag Action */}
                <div className="flex items-center justify-between gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-black ${currentQuestion.category === 'Sahsiah & Adab Kependekaran'
                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                    : currentQuestion.category === 'Silat & Kemahiran Silibus'
                      ? 'bg-red-50 text-red-800 border border-red-200'
                      : 'bg-blue-50 text-blue-800 border border-blue-200'
                    }`}>
                    {currentQuestion.category}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleToggleFlag(currentQuestion.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition cursor-pointer border ${flaggedQuestions.has(currentQuestion.id)
                      ? 'bg-amber-500 text-slate-950 border-amber-600'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${flaggedQuestions.has(currentQuestion.id) ? 'fill-current' : ''}`} />
                    <span>{flaggedQuestions.has(currentQuestion.id) ? 'Ditanda Semak' : 'Tanda untuk Semakan'}</span>
                  </button>
                </div>

                {/* Question Statement */}
                <h3 className="text-base sm:text-xl font-extrabold text-slate-900 leading-relaxed">
                  {currentQuestion.question}
                </h3>

                {/* 4 Options Radio List (A, B, C, D) */}
                <div className="space-y-3 pt-2">
                  {currentQuestion.options.map((optionText, optIdx) => {
                    const isSelected = userAnswers[currentQuestion.id] === optIdx;
                    const optionLabels = ['A', 'B', 'C', 'D'];

                    return (
                      <div
                        key={optIdx}
                        onClick={() => handleSelectOption(currentQuestion.id, optIdx)}
                        className={`p-4 sm:p-5 rounded-2xl border-2 transition-all cursor-pointer flex items-center gap-3.5 ${isSelected
                          ? 'border-red-600 bg-red-50/50 shadow-xs ring-1 ring-red-600'
                          : 'border-slate-200/90 bg-white hover:border-slate-300 hover:bg-slate-50/50'
                          }`}
                      >
                        <div
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-black text-xs shrink-0 transition ${isSelected
                            ? 'bg-red-600 text-white shadow-xs'
                            : 'bg-slate-100 text-slate-600'
                            }`}
                        >
                          {optionLabels[optIdx]}
                        </div>
                        <span className={`text-xs sm:text-sm font-semibold flex-1 leading-snug ${isSelected ? 'text-slate-900 font-bold' : 'text-slate-700'}`}>
                          {optionText}
                        </span>
                        {isSelected && (
                          <Check className="w-5 h-5 text-red-600 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  type="button"
                  disabled={currentQuestionIndex === 0}
                  onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
                  className="px-5 py-2.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Soalan Sebelumnya</span>
                </button>

                {currentQuestionIndex < TOTAL_EXAM_QUESTIONS - 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentQuestionIndex((prev) => Math.min(TOTAL_EXAM_QUESTIONS - 1, prev + 1))}
                    className="px-6 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                  >
                    <span>Soalan Seterusnya</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSubmitModalOpen(true)}
                    className="px-6 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-black text-xs flex items-center gap-1.5 transition cursor-pointer shadow-sm"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Semak & Hantar</span>
                  </button>
                )}
              </div>
            </div>

            {/* Right Question Palette (1 col on desktop, Minimized by default) */}
            <div className="lg:col-span-1 space-y-4">
              {!isPaletteExpanded ? (
                /* MINIMIZED TIDY PALETTE (Default) */
                <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5 shadow-xs space-y-3.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                        <LayoutGrid className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                          Palet Soalan
                        </h4>
                        <span className="text-[10px] text-slate-500 font-semibold">
                          50 Soalan Objektif
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-black px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {answeredCount}/50
                    </span>
                  </div>

                  {/* Micro Progress Bar */}
                  <div className="space-y-1">
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="bg-red-600 h-full rounded-full transition-all duration-300"
                        style={{ width: `${(answeredCount / TOTAL_EXAM_QUESTIONS) * 100}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
                      <span>{Math.round((answeredCount / TOTAL_EXAM_QUESTIONS) * 100)}% siap</span>
                      <span>Baki {TOTAL_EXAM_QUESTIONS - answeredCount}</span>
                    </div>
                  </div>

                  {/* Mini Stats Summary Pill Box */}
                  <div className="grid grid-cols-3 gap-1.5 text-center p-2 rounded-2xl bg-slate-50 border border-slate-100 text-[10px]">
                    <div>
                      <span className="block font-black text-slate-900">{answeredCount}</span>
                      <span className="text-slate-500 text-[9px]">Dijawab</span>
                    </div>
                    <div>
                      <span className="block font-black text-slate-900">{TOTAL_EXAM_QUESTIONS - answeredCount}</span>
                      <span className="text-slate-500 text-[9px]">Belum</span>
                    </div>
                    <div>
                      <span className="block font-black text-amber-600">{flaggedQuestions.size}</span>
                      <span className="text-slate-500 text-[9px]">Ditanda</span>
                    </div>
                  </div>

                  {/* Expand Palette Trigger Button */}
                  <button
                    type="button"
                    onClick={() => setIsPaletteExpanded(true)}
                    className="w-full py-2 px-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-extrabold text-xs transition cursor-pointer flex items-center justify-center gap-1.5 border border-slate-200/80"
                  >
                    <LayoutGrid className="w-3.5 h-3.5 text-red-600" />
                    <span>Buka Palet 50 Soalan</span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                  </button>

                  {/* Jump to Next Unanswered */}
                  {answeredCount < TOTAL_EXAM_QUESTIONS && (
                    <button
                      type="button"
                      onClick={() => {
                        const nextUnansweredIdx = activeExamQuestions.findIndex(
                          (q) => userAnswers[q.id] === undefined
                        );
                        if (nextUnansweredIdx !== -1) {
                          setCurrentQuestionIndex(nextUnansweredIdx);
                        }
                      }}
                      className="w-full text-center text-[11px] font-bold text-blue-600 hover:text-blue-700 py-1 transition cursor-pointer"
                    >
                      Lompat ke Soalan Belum Jawab →
                    </button>
                  )}
                </div>
              ) : (
                /* EXPANDED TIDY PALETTE */
                <div className="bg-white rounded-3xl border border-slate-200 p-3.5 sm:p-4 shadow-xs space-y-3 animate-fade-in">
                  {/* Palette Header */}
                  <div className="flex items-center justify-between gap-1 pb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0 shadow-xs">
                        <LayoutGrid className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <h4 className="text-[11px] font-black text-slate-900 uppercase tracking-wide leading-none whitespace-nowrap">
                          Palet Soalan
                        </h4>
                        <span className="text-[10px] text-slate-500 font-bold block mt-0.5 whitespace-nowrap">
                          {answeredCount}/50 Dijawab
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsPaletteExpanded(false)}
                      className="px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[10px] flex items-center gap-1 transition cursor-pointer border border-slate-200/60 shrink-0"
                      title="Kecilkan palet soalan"
                    >
                      <span>Kecilkan</span>
                      <ChevronUp className="w-3 h-3 text-slate-500" />
                    </button>
                  </div>

                  {/* Tidy 2x2 Legend Grid (No truncation, neat pills) */}
                  <div className="grid grid-cols-2 gap-1.5 text-[10px] font-bold">
                    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl bg-red-50/80 border border-red-200/70 text-red-900 whitespace-nowrap">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-600 shrink-0" />
                      <span>Jawab: {answeredCount}</span>
                    </div>

                    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl bg-slate-50 border border-slate-200/80 text-slate-800 whitespace-nowrap">
                      <span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-slate-300 shrink-0" />
                      <span>Belum: {TOTAL_EXAM_QUESTIONS - answeredCount}</span>
                    </div>

                    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl bg-amber-50/80 border border-amber-200/70 text-amber-900 whitespace-nowrap">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0" />
                      <span>Tanda: {flaggedQuestions.size}</span>
                    </div>

                    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl bg-blue-50/80 border border-blue-200/70 text-blue-900 whitespace-nowrap">
                      <span className="w-2.5 h-2.5 rounded-full ring-2 ring-blue-600 bg-white shrink-0" />
                      <span>Semasa</span>
                    </div>
                  </div>

                  {/* Quick Filter Tabs */}
                  <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-xl text-[10px] font-bold text-center">
                    <button
                      type="button"
                      onClick={() => setPaletteFilter('all')}
                      className={`py-1 px-1 rounded-lg transition cursor-pointer whitespace-nowrap ${paletteFilter === 'all'
                        ? 'bg-white text-slate-900 shadow-2xs font-black'
                        : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      Semua
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaletteFilter('unanswered')}
                      className={`py-1 px-1 rounded-lg transition cursor-pointer whitespace-nowrap ${paletteFilter === 'unanswered'
                        ? 'bg-white text-slate-900 shadow-2xs font-black'
                        : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      Belum ({TOTAL_EXAM_QUESTIONS - answeredCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaletteFilter('flagged')}
                      className={`py-1 px-1 rounded-lg transition cursor-pointer whitespace-nowrap ${paletteFilter === 'flagged'
                        ? 'bg-white text-slate-900 shadow-2xs font-black'
                        : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      Tanda ({flaggedQuestions.size})
                    </button>
                  </div>

                  {/* 50 Bubbles Grid (Tidy & Compact) */}
                  <div className="grid grid-cols-5 gap-1.5 max-h-[320px] overflow-y-auto pr-1">
                    {activeExamQuestions.map((q, idx) => {
                      const isAnswered = userAnswers[q.id] !== undefined;
                      const isFlagged = flaggedQuestions.has(q.id);
                      const isCurrent = currentQuestionIndex === idx;

                      // Filter logic for quick searching
                      if (paletteFilter === 'unanswered' && isAnswered) return null;
                      if (paletteFilter === 'flagged' && !isFlagged) return null;

                      return (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => setCurrentQuestionIndex(idx)}
                          className={`h-8 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center relative ${isCurrent
                            ? 'ring-2 ring-blue-600 bg-blue-50 text-blue-900 font-black shadow-xs'
                            : isFlagged
                              ? 'bg-amber-100 text-amber-900 border border-amber-300 font-extrabold'
                              : isAnswered
                                ? 'bg-red-600 text-white font-black'
                                : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                            }`}
                        >
                          {idx + 1}
                          {isFlagged && (
                            <span className="w-2 h-2 rounded-full bg-amber-500 absolute -top-0.5 -right-0.5 ring-1 ring-white" />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Submit Trigger */}
                  <button
                    type="button"
                    onClick={() => setIsSubmitModalOpen(true)}
                    className="w-full py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs transition cursor-pointer shadow-xs mt-2"
                  >
                    Selesai & Hantar Peperiksaan
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* VIEW C: RESULTS & REVIEW VIEW (ZERO-MODAL WORKFLOW)       */}
      {/* ======================================================== */}
      {viewState === 'results' && selectedAttemptForReview && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8 animate-fade-in">
          {/* Results Hero Card */}
          <div className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-10 shadow-xs relative text-center">
            {/* Action Top Right */}
            <div className="sm:absolute sm:top-6 sm:right-6 flex items-center justify-center gap-2 mb-4 sm:mb-0">
              <button
                type="button"
                onClick={() => setViewState('briefing')}
                className="px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-xs"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Kembali ke Taklimat</span>
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs flex items-center gap-1.5 transition cursor-pointer"
              >
                <Printer className="w-3.5 h-3.5" />
                <span>Cetak Slip Keputusan</span>
              </button>
            </div>

            {/* Overlapping Result Badge */}
            <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto flex items-center justify-center ring-4 ring-white shadow-md font-black text-2xl sm:text-3xl text-white ${selectedAttemptForReview.isPassed ? 'bg-emerald-600' : 'bg-red-600'
              }`}>
              {selectedAttemptForReview.percentage}%
            </div>

            <div className="mt-4">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${selectedAttemptForReview.isPassed ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                {selectedAttemptForReview.isPassed ? '✓ KEPUTUSAN: LULUS' : '✗ KEPUTUSAN: MENGULANG'}
              </span>

              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mt-2">
                {selectedAttemptForReview.isPassed ? 'Tahniah! Calon Berjaya Lulus Ujian Teori' : 'Perlu Ulangkaji Silibus'}
              </h2>

              {/* Candidate Info Pill */}
              <div className="mt-2.5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 text-xs font-bold border border-slate-200">
                <User className="w-3.5 h-3.5 text-slate-500" />
                <span>Calon: <strong className="text-slate-900">{selectedAttemptForReview.userName}</strong></span>
                <span>•</span>
                <span>{selectedAttemptForReview.gelanggang}</span>
                <span>•</span>
                <span>{selectedAttemptForReview.bengkung}</span>
              </div>

              <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1.5">
                Gred: <span className="font-extrabold text-slate-900">{selectedAttemptForReview.grade}</span> • Skor: <span className="font-bold text-slate-900">{selectedAttemptForReview.score}</span> daripada {TOTAL_EXAM_QUESTIONS} soalan betul
              </p>
            </div>

            {/* Floating Metric Pill Bar */}
            <div className="bg-white rounded-2xl sm:rounded-full py-2.5 px-6 shadow-xs border border-slate-200/80 grid grid-cols-3 gap-4 max-w-lg mx-auto mt-6">
              <div className="text-center">
                <span className="text-base sm:text-lg font-bold text-slate-900">
                  Percubaan {selectedAttemptForReview.attemptNumber}
                </span>
                <span className="text-[11px] text-slate-400 font-medium block">Peringkat</span>
              </div>
              <div className="text-center border-x border-slate-200">
                <span className="text-base sm:text-lg font-bold text-slate-900">
                  {Math.floor(selectedAttemptForReview.timeSpentSeconds / 60)}m {selectedAttemptForReview.timeSpentSeconds % 60}s
                </span>
                <span className="text-[11px] text-slate-400 font-medium block">Masa Digunakan</span>
              </div>
              <div className="text-center">
                <span className="text-base sm:text-lg font-bold text-slate-900">
                  {TOTAL_EXAM_QUESTIONS - selectedAttemptForReview.score}
                </span>
                <span className="text-[11px] text-slate-400 font-medium block">Kesilapan</span>
              </div>
            </div>

            {/* Retest CTA Prompt */}
            {hasRemainingAttempts && (
              <div className="mt-8 pt-6 border-t border-slate-100 max-w-md mx-auto">
                <p className="text-xs text-slate-600 font-medium mb-3">
                  Anda masih mempunyai <strong className="text-slate-900">1 kali percubaan lagi</strong>. Markah tertinggi antara kedua-dua percubaan akan diambil sebagai markah rasmi penilaian bengkung.
                </p>
                <button
                  type="button"
                  onClick={() => setIsStartConfirmModalOpen(true)}
                  className="px-6 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs shadow-md transition cursor-pointer flex items-center justify-center gap-2 mx-auto"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Ambil Percubaan Ke-2 Sekarang</span>
                </button>
              </div>
            )}
          </div>

          {/* 3 Domain Category Performance Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sahsiah */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-amber-700 uppercase tracking-wide">
                  Bahagian A: Sahsiah & Adab
                </span>
                <span className="text-sm font-black text-slate-900">
                  {selectedAttemptForReview.categoryBreakdown?.sahsiah?.percentage || 0}%
                </span>
              </div>
              <div className="mt-2 text-xl font-black text-slate-900">
                {selectedAttemptForReview.categoryBreakdown?.sahsiah?.correct || 0} / {selectedAttemptForReview.categoryBreakdown?.sahsiah?.total || 18} Betul
              </div>
              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-3">
                <div
                  className="bg-amber-500 h-full rounded-full"
                  style={{ width: `${selectedAttemptForReview.categoryBreakdown?.sahsiah?.percentage || 0}%` }}
                />
              </div>
            </div>

            {/* Silat */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-red-700 uppercase tracking-wide">
                  Bahagian B: Silat & Silibus
                </span>
                <span className="text-sm font-black text-slate-900">
                  {selectedAttemptForReview.categoryBreakdown?.silat?.percentage || 0}%
                </span>
              </div>
              <div className="mt-2 text-xl font-black text-slate-900">
                {selectedAttemptForReview.categoryBreakdown?.silat?.correct || 0} / {selectedAttemptForReview.categoryBreakdown?.silat?.total || 18} Betul
              </div>
              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-3">
                <div
                  className="bg-red-600 h-full rounded-full"
                  style={{ width: `${selectedAttemptForReview.categoryBreakdown?.silat?.percentage || 0}%` }}
                />
              </div>
            </div>

            {/* Sejarah & Falsafah ASMM */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-blue-700 uppercase tracking-wide">
                  Bahagian C: Sejarah & Falsafah
                </span>
                <span className="text-sm font-black text-slate-900">
                  {selectedAttemptForReview.categoryBreakdown?.persatuan?.percentage || 0}%
                </span>
              </div>
              <div className="mt-2 text-xl font-black text-slate-900">
                {selectedAttemptForReview.categoryBreakdown?.persatuan?.correct || 0} / {selectedAttemptForReview.categoryBreakdown?.persatuan?.total || 14} Betul
              </div>
              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-3">
                <div
                  className="bg-blue-600 h-full rounded-full"
                  style={{ width: `${selectedAttemptForReview.categoryBreakdown?.persatuan?.percentage || 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* Anti-Cheating Secrecy Banner or Unlocked Confirmation Banner */}
          {!isAnswerKeyRevealedToAll ? (
            <div className="bg-white rounded-3xl border border-amber-200/90 p-6 sm:p-7 shadow-xs relative overflow-hidden">
              <div className="absolute top-0 left-0 h-1.5 w-full bg-linear-to-r from-red-600 via-amber-500 to-blue-600" />
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500 text-slate-900 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                    <Lock className="w-6 h-6" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-base sm:text-lg font-black text-slate-900">
                        Skema Jawapan & Penjelasan Silibus Dirahsiakan
                      </h4>
                      <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 uppercase tracking-wider">
                        Integriti Peperiksaan
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed max-w-2xl">
                      Demi memelihara keadilan dan mengelakkan ketirisan soalan, <strong>jawapan yang betul dan huraian silibus dirahsiakan</strong> sehingga kesemua calon Ujian Bengkung selesai menduduki peperiksaan ini.
                    </p>

                    {/* Live Progress Pill & Bar */}
                    <div className="pt-2 space-y-2">
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-700 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-900 font-black border border-slate-200">
                          <Users className="w-3.5 h-3.5 text-blue-600" />
                          <span>{completionStatus.completedCount} / {completionStatus.totalCandidates || 15} Calon Telah Menjawab ({completionPercentage}%)</span>
                        </span>
                        {completionStatus.remainingCount > 0 ? (
                          <span className="text-xs text-amber-800 font-semibold">
                            (Baki {completionStatus.remainingCount} calon belum menduduki)
                          </span>
                        ) : (
                          <span className="text-xs text-emerald-700 font-semibold">
                            (Semua calon selesai menduduki)
                          </span>
                        )}
                      </div>

                      <div className="w-full max-w-md bg-slate-100 h-2 rounded-full overflow-hidden border border-slate-200">
                        <div
                          className="bg-amber-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, Math.max(5, completionPercentage))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Management Override & Preview Controls */}
                {isManagement && (
                  <div className="flex flex-col sm:items-end gap-2 shrink-0 pt-3 md:pt-0 border-t md:border-t-0 border-slate-100">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setIsExaminerPreview(!isExaminerPreview)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer border flex items-center gap-1.5 ${isExaminerPreview
                          ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                          : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                          }`}
                        title="Tukar antara mod semakan penguji dan simulasi pandangan calon"
                      >
                        {isExaminerPreview ? <Eye className="w-3.5 h-3.5 text-blue-600" /> : <EyeOff className="w-3.5 h-3.5 text-slate-500" />}
                        <span>{isExaminerPreview ? 'Mod Penguji (Papar Skema)' : 'Mod Calon (Sembunyi)'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleToggleManualKeyUnlock}
                        className="px-3.5 py-1.5 rounded-xl text-xs font-black transition cursor-pointer bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1.5 shadow-xs"
                      >
                        <Unlock className="w-3.5 h-3.5 text-amber-400" />
                        <span>Buka Skema Untuk Calon</span>
                      </button>
                    </div>
                    <span className="text-[10px] text-slate-500 font-semibold">
                      *Akses Penguji / Pentadbir Sistem
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-emerald-200 p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm sm:text-base font-extrabold text-slate-900">
                      Skema Jawapan Rasmi Telah Dibuka
                    </h4>
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                      SELESAI
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 font-medium mt-0.5">
                    Kesemua calon Ujian Bengkung telah selesai menduduki ujian (atau dibuka oleh pentadbir). Anda kini boleh menyemak skema jawapan tepat beserta penjelasan silibus rasmi.
                  </p>
                </div>
              </div>

              {isManagement && (
                <button
                  type="button"
                  onClick={handleToggleManualKeyUnlock}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-red-600 hover:bg-red-50 border border-red-200 transition cursor-pointer shrink-0"
                >
                  Kunci Semula Skema
                </button>
              )}
            </div>
          )}

          {/* Question-by-Question Review Accordion */}
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-extrabold text-slate-900">
                  {isAnswerKeyVisible ? 'Semakan Jawapan Terperinci & Skema Silibus' : 'Semakan Pilihan Jawapan Anda'}
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  {isAnswerKeyVisible
                    ? 'Semak jawapan anda bersama skema jawapan tepat dan penjelasan silibus rasmi persatuan.'
                    : 'Pilihan jawapan yang telah anda rekodkan bagi setiap soalan. Skema jawapan betul akan dipaparkan sebaik sahaja semua calon selesai.'}
                </p>
              </div>

              {/* Review Filter Pills */}
              {isAnswerKeyVisible ? (
                <div className="flex items-center gap-1.5 self-start sm:self-auto bg-slate-100 p-1 rounded-full text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setReviewFilter('all')}
                    className={`px-3 py-1 rounded-full transition cursor-pointer ${reviewFilter === 'all' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                      }`}
                  >
                    Semua ({TOTAL_EXAM_QUESTIONS})
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewFilter('wrong')}
                    className={`px-3 py-1 rounded-full transition cursor-pointer ${reviewFilter === 'wrong' ? 'bg-red-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                      }`}
                  >
                    Salah Sahaja ({TOTAL_EXAM_QUESTIONS - selectedAttemptForReview.score})
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 self-start sm:self-auto bg-slate-100 p-1 rounded-full text-xs font-bold">
                  <span className="px-3 py-1 rounded-full bg-white text-slate-900 shadow-xs font-extrabold">
                    {TOTAL_EXAM_QUESTIONS} Soalan Direkodkan
                  </span>
                </div>
              )}
            </div>

            {/* Questions Review List */}
            <div className="space-y-4">
              {displayedReviewQuestions.map((q) => {
                const userChoice = selectedAttemptForReview.answers[q.id];
                const isCorrect = userChoice === q.correctAnswer;
                const optionLabels = ['A', 'B', 'C', 'D'];

                if (!isAnswerKeyVisible) {
                  // SECURE VIEW: Hide correct answers and explanations until all candidates have taken the exam
                  return (
                    <div
                      key={q.id}
                      className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 transition shadow-xs"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black bg-slate-900 text-white">
                            {q.id}
                          </span>
                          <span className="text-xs font-black text-slate-900">
                            Soalan {q.id}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {q.category}
                          </span>
                        </div>

                        <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 flex items-center gap-1.5">
                          <Check className="w-3 h-3 text-slate-600" />
                          <span>Pilihan Direkodkan</span>
                        </span>
                      </div>

                      <h4 className="text-sm sm:text-base font-bold text-slate-900 mt-2.5 leading-snug">
                        {q.question}
                      </h4>

                      {/* Options list showing user selection without revealing correct answer */}
                      <div className="mt-3 space-y-2">
                        {q.options.map((optText, optIdx) => {
                          const isThisUserChoice = userChoice === optIdx;

                          return (
                            <div
                              key={optIdx}
                              className={`p-2.5 sm:p-3 rounded-xl text-xs font-semibold flex items-center gap-2.5 border transition ${isThisUserChoice
                                ? 'border-slate-900 bg-slate-900 text-white font-bold shadow-xs'
                                : 'border-slate-100 bg-slate-50/80 text-slate-700'
                                }`}
                            >
                              <span
                                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${isThisUserChoice
                                  ? 'bg-white text-slate-900'
                                  : 'bg-slate-200 text-slate-700'
                                  }`}
                              >
                                {optionLabels[optIdx]}
                              </span>
                              <span className="flex-1 leading-snug">{optText}</span>
                              {isThisUserChoice && (
                                <span className="text-[10px] font-black text-amber-400 uppercase shrink-0">
                                  Pilihan Anda
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Confidentiality footer */}
                      <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200/80 text-xs text-slate-500 font-medium flex items-center gap-2">
                        <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>Skema jawapan tepat & ulasan silibus dirahsiakan sehingga semua calon selesai.</span>
                      </div>
                    </div>
                  );
                }

                // UNLOCKED VIEW: Full details, correct answers, and syllabus explanations
                return (
                  <div
                    key={q.id}
                    className={`rounded-2xl border p-4 sm:p-5 transition ${isCorrect ? 'border-slate-200 bg-white' : 'border-red-200 bg-red-50/20'
                      }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${isCorrect ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
                            }`}
                        >
                          {isCorrect ? '✓' : '✗'}
                        </span>
                        <span className="text-xs font-black text-slate-900">
                          Soalan {q.id}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {q.category}
                        </span>
                      </div>

                      <span
                        className={`text-[11px] font-black px-2.5 py-0.5 rounded-full ${isCorrect ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {isCorrect ? 'Betul (+1 Markah)' : 'Salah (0 Markah)'}
                      </span>
                    </div>

                    <h4 className="text-sm sm:text-base font-bold text-slate-900 mt-2.5 leading-snug">
                      {q.question}
                    </h4>

                    {/* Options list showing user selection vs correct answer */}
                    <div className="mt-3 space-y-2">
                      {q.options.map((optText, optIdx) => {
                        const isThisUserChoice = userChoice === optIdx;
                        const isThisCorrect = q.correctAnswer === optIdx;

                        return (
                          <div
                            key={optIdx}
                            className={`p-2.5 sm:p-3 rounded-xl text-xs font-semibold flex items-center gap-2.5 border ${isThisCorrect
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-950 font-bold'
                              : isThisUserChoice
                                ? 'border-red-400 bg-red-100/70 text-red-950 font-bold'
                                : 'border-slate-100 bg-slate-50 text-slate-600 opacity-80'
                              }`}
                          >
                            <span
                              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${isThisCorrect
                                ? 'bg-emerald-600 text-white'
                                : isThisUserChoice
                                  ? 'bg-red-600 text-white'
                                  : 'bg-slate-200 text-slate-700'
                                }`}
                            >
                              {optionLabels[optIdx]}
                            </span>
                            <span className="flex-1 leading-snug">{optText}</span>
                            {isThisCorrect && (
                              <span className="text-[10px] font-black text-emerald-700 uppercase shrink-0">
                                Jawapan Tepat
                              </span>
                            )}
                            {isThisUserChoice && !isThisCorrect && (
                              <span className="text-[10px] font-black text-red-700 uppercase shrink-0">
                                Pilihan Anda
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Explanation */}
                    {q.explanation && (
                      <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200/80 text-xs text-slate-600 leading-relaxed">
                        <strong className="text-slate-900 font-bold">Penjelasan Silibus: </strong>
                        {q.explanation}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* VIEW D: MANAGEMENT & EXAMINER ROSTER (ALL CANDIDATES)    */}
      {/* ======================================================== */}
      {viewState === 'roster' && isManagement && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 animate-fade-in">
          {/* Standardized Hero Feature Card */}
          <GradingHeaderHero
            activeTab="ujian-bertulis"
            badgeTag="Papan Rekod Peperiksaan Teori & Sahsiah"
            badgeIcon={ShieldCheck}
            badgeColorClass="bg-blue-50 text-blue-700 border-blue-200/80"
            title="Rekod Calon Ujian Bertulis"
            subtitle="Penyata semakan markah teori seluruh gelanggang bagi calon penilaian kenaikan bengkung ASMM."
            metrics={[
              { value: filteredRoster.length, label: 'Calon Mengambil', colorClass: 'text-slate-900' },
              { value: completionStatus.completedCount, label: 'Selesai Dinilai', colorClass: 'text-blue-600' },
              { value: filteredRoster.filter(r => r.isPassed).length, label: 'Lulus', colorClass: 'text-amber-600' },
              { value: filteredRoster.filter(r => !r.isPassed).length, label: 'Mengulang', colorClass: 'text-red-600' },
            ]}
          >
            {/* View State Switcher: Ujian Saya vs Rekod Calon */}
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="inline-flex items-center gap-1 bg-slate-100 p-1 rounded-2xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => setViewState('briefing')}
                  className="px-3.5 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer text-slate-600 hover:text-slate-900 hover:bg-white"
                >
                  Ujian Saya
                </button>
                <button
                  type="button"
                  onClick={() => setViewState('roster')}
                  className="px-3.5 py-1.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 bg-slate-900 text-white shadow-xs"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-amber-500" />
                  <span>Rekod Calon ({allAttemptsList.length})</span>
                </button>
              </div>
            </div>
          </GradingHeaderHero>

          {/* Cohort Progress & Integrity Controller Card */}
          <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-extrabold text-slate-900">
                    Status Integriti Skema Ujian ({completionStatus.completedCount} / {completionStatus.totalCandidates || 15} Calon Selesai)
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    Status Skema Calon:{' '}
                    <span className={`font-bold ${isAnswerKeyRevealedToAll ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {isAnswerKeyRevealedToAll ? 'Terbuka (Calon Boleh Semak Jawapan Betul)' : 'Dirahsiakan (Calon Belum Boleh Lihat Skema)'}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleToggleManualKeyUnlock}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer border flex items-center gap-2 ${completionStatus.isKeyUnlocked
                    ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                    : 'bg-slate-900 border-slate-900 text-white hover:bg-slate-800'
                    }`}
                >
                  {completionStatus.isKeyUnlocked ? (
                    <>
                      <Lock className="w-3.5 h-3.5" />
                      <span>Kunci Semula Skema Calon</span>
                    </>
                  ) : (
                    <>
                      <Unlock className="w-3.5 h-3.5 text-amber-400" />
                      <span>Buka Skema Untuk Semua Calon</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Cari nama atau ID calon..."
                value={rosterSearch}
                onChange={(e) => setRosterSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 focus:outline-none focus:border-red-600 bg-slate-50"
              />
            </div>

            <select
              value={rosterGelanggangFilter}
              onChange={(e) => setRosterGelanggangFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-red-600"
            >
              <option value="all">Semua Gelanggang</option>
              <option value="Pusaka Warisan">Pusaka Warisan</option>
              <option value="Wadi AnNour">Wadi AnNour</option>
              <option value="Paya Besar">Paya Besar</option>
              <option value="ASMM">Pusat ASMM</option>
            </select>

            <select
              value={rosterStatusFilter}
              onChange={(e) => setRosterStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-red-600"
            >
              <option value="all">Semua Status</option>
              <option value="passed">Lulus Sahaja</option>
              <option value="failed">Mengulang Sahaja</option>
            </select>
          </div>

          {/* Candidates Attempts Table */}
          {filteredRoster.length === 0 ? (
            <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center text-slate-500 text-xs">
              Tiada rekod percubaan calon yang sepadan dengan kriteria carian.
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-extrabold uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="py-3 px-4">Calon</th>
                      <th className="py-3 px-4">Gelanggang</th>
                      <th className="py-3 px-4">Percubaan</th>
                      <th className="py-3 px-4">Skor / Markah</th>
                      <th className="py-3 px-4">Keputusan</th>
                      <th className="py-3 px-4">Tarikh Selesai</th>
                      <th className="py-3 px-4 text-right">Tindakan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium">
                    {filteredRoster.map((att) => (
                      <tr key={att.id} className="hover:bg-slate-50/50 transition">
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-900">{att.userName}</div>
                          <div className="text-[11px] text-slate-400 font-mono">{att.memberId}</div>
                        </td>
                        <td className="py-3.5 px-4 text-slate-600 font-semibold">{att.gelanggang}</td>
                        <td className="py-3.5 px-4 font-bold text-slate-700">Percubaan {att.attemptNumber}</td>
                        <td className="py-3.5 px-4">
                          <span className="font-black text-slate-900 text-sm">{att.percentage}%</span>
                          <span className="text-[10px] text-slate-500 ml-1.5">({att.score}/{att.totalQuestions})</span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black ${att.isPassed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                            {att.isPassed ? 'LULUS' : 'MENGULANG'}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-slate-500 text-[11px]">
                          {new Date(att.completedAt).toLocaleString('ms-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedAttemptForReview(att);
                                setViewState('results');
                              }}
                              className="px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] transition cursor-pointer"
                            >
                              Lihat Jawapan
                            </button>
                            {isUserSuperAdmin && (
                              <button
                                type="button"
                                onClick={() => setAttemptToDelete(att)}
                                className="p-1.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition cursor-pointer"
                                title="Padam Percubaan Calon Ini (Superadmin Sahaja)"
                                aria-label="Padam percubaan"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 1: START EXAM CONFIRMATION (LOCK BODY SCROLL)       */}
      {/* ======================================================== */}
      {isStartConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-xl space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6" />
            </div>

            <div className="text-center">
              <h3 className="text-lg font-extrabold text-slate-900">
                Mulakan Percubaan {attemptsCount + 1}?
              </h3>
              <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                Sebaik sahaja anda menekan mula, timer <strong>75 minit</strong> akan terus berjalan secara automatik. Pastikan anda berada dalam suasana kondusif dan bersedia sepenuhnya.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 text-xs space-y-2 border border-slate-200">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200/80">
                <span className="text-slate-600 font-semibold">Calon Menjawab:</span>
                <span className="font-extrabold text-slate-900 flex items-center gap-1.5">
                  <span>{activeCandidate?.name || currentUser?.name}</span>
                  {activeCandidate?.id !== currentUser?.id && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">
                      {activeCandidate?.relationship || 'Ahli Keluarga'}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Bilangan Soalan:</span>
                <span className="font-extrabold text-slate-900">50 Soalan Objektif (Rawak)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Masa Diperuntukkan:</span>
                <span className="font-extrabold text-red-600">75 Minit</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Percubaan Yang Digunakan:</span>
                <span className="font-extrabold text-slate-900">Percubaan {attemptsCount + 1} daripada 2</span>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsStartConfirmModalOpen(false)}
                className="flex-1 py-2.5 rounded-full border border-slate-200 text-slate-700 font-bold text-xs hover:bg-slate-50 transition cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleStartExam}
                className="flex-1 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs shadow-md transition cursor-pointer"
              >
                Ya, Mula Sekarang
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 2: SUBMIT EXAM CONFIRMATION (LOCK BODY SCROLL)      */}
      {/* ======================================================== */}
      {isSubmitModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-xl space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-6 h-6" />
            </div>

            <div className="text-center">
              <h3 className="text-lg font-extrabold text-slate-900">
                Sahkan Penghantaran Jawapan?
              </h3>
              <p className="text-xs text-slate-500 font-medium mt-1">
                Sila pastikan anda telah menyemak semua jawapan sebelum mengesahkan penghantaran rasmi.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 text-xs space-y-2 border border-slate-200">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200/80">
                <span className="text-slate-600 font-semibold">Calon:</span>
                <span className="font-extrabold text-slate-900 flex items-center gap-1.5">
                  <span>{activeCandidate?.name || currentUser?.name}</span>
                  {activeCandidate?.id !== currentUser?.id && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">
                      {activeCandidate?.relationship || 'Ahli Keluarga'}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Soalan Telah Dijawab:</span>
                <span className={`font-black ${answeredCount < TOTAL_EXAM_QUESTIONS ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {answeredCount} / {TOTAL_EXAM_QUESTIONS}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Soalan Belum Dijawab:</span>
                <span className="font-extrabold text-slate-900">
                  {TOTAL_EXAM_QUESTIONS - answeredCount}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Soalan Ditanda Semak:</span>
                <span className="font-extrabold text-amber-600">
                  {flaggedQuestions.size}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Baki Masa Peperiksaan:</span>
                <span className="font-mono font-bold text-slate-900">
                  {formattedTimer}
                </span>
              </div>
            </div>

            {answeredCount < TOTAL_EXAM_QUESTIONS && (
              <p className="text-[11px] text-amber-700 bg-amber-50 p-2.5 rounded-xl border border-amber-200 font-semibold leading-relaxed">
                Perhatian: Terdapat {TOTAL_EXAM_QUESTIONS - answeredCount} soalan yang belum anda jawab. Soalan tidak dijawab dikira sebagai markah 0.
              </p>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsSubmitModalOpen(false)}
                className="flex-1 py-2.5 rounded-full border border-slate-200 text-slate-700 font-bold text-xs hover:bg-slate-50 transition cursor-pointer"
              >
                Kembali Semak
              </button>
              <button
                type="button"
                onClick={() => handleFinalizeSubmit(false)}
                className="flex-1 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-black text-xs shadow-md transition cursor-pointer"
              >
                Ya, Hantar Rasmi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 3: SUPERADMIN DELETE ATTEMPT (LOCK BODY SCROLL)    */}
      {/* ======================================================== */}
      {attemptToDelete && isUserSuperAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-black uppercase tracking-wider mb-1.5">
                Akses Eksklusif Superadmin
              </span>
              <h3 className="text-lg font-extrabold text-slate-900">
                Padam Rekod Percubaan?
              </h3>
              <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                Adakah anda pasti ingin memadam rekod percubaan ini secara kekal daripada sistem pangkalan data?
              </p>
            </div>

            {/* Candidate & Attempt Details Card */}
            <div className="bg-slate-50 rounded-2xl p-4 text-xs space-y-2 border border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-slate-500 font-semibold">Nama Calon:</span>
                <span className="font-extrabold text-slate-900">{attemptToDelete.userName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 font-semibold">No. ID / IC:</span>
                <span className="font-mono text-slate-700">{attemptToDelete.memberId}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 font-semibold">Percubaan:</span>
                <span className="font-black text-slate-900">Percubaan {attemptToDelete.attemptNumber}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 font-semibold">Skor / Keputusan:</span>
                <span className="font-bold text-slate-900">
                  {attemptToDelete.percentage}% ({attemptToDelete.score}/{attemptToDelete.totalQuestions}) — {attemptToDelete.isPassed ? 'LULUS' : 'MENGULANG'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 font-semibold">Tarikh Selesai:</span>
                <span className="text-slate-600">
                  {new Date(attemptToDelete.completedAt).toLocaleString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>

            {/* Warning Message */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 text-left">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800 leading-relaxed font-medium">
                <strong>Perhatian:</strong> Rekod ini akan dipadam daripada storan tempatan dan pelayan aaPanel. Had kuota percubaan calon akan dipulihkan secara automatik.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                disabled={isDeletingAttempt}
                onClick={() => setAttemptToDelete(null)}
                className="flex-1 py-2.5 rounded-full border border-slate-200 text-slate-700 font-bold text-xs hover:bg-slate-50 transition cursor-pointer disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isDeletingAttempt}
                onClick={handleConfirmDeleteAttempt}
                className="flex-1 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-black text-xs shadow-md transition cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {isDeletingAttempt ? (
                  <span>Memadam...</span>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Ya, Sahkan Padam</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
